'use client';

import { type FormEvent, useEffect, useState } from 'react';
import { DatePicker } from '@core/ui/datepicker';
import { Skeleton } from '@core/ui/skeleton';
import {
  getPublicShipments,
  type PublicShipment,
  type PublicShipmentPagination,
} from '@/server/api';
import {
  PiArrowLeft,
  PiArrowRight,
  PiCaretDownBold,
  PiEnvelopeSimpleBold,
  PiGavelBold,
  PiMagnifyingGlass,
  PiMagnifyingGlassMinus,
  PiPhoneBold,
} from 'react-icons/pi';
import { Badge, Button, Empty, Input, Select } from 'rizzui';
import { Pill } from '@/app/shared/landing/pages/components/sub-page-sections';
import { AvailableLoadBidModal } from './available-load-bid-modal';
import { AvailableLoadBookingModal } from './available-load-booking-modal';
import { CustomerBidModal } from '@/app/shared/customer-bid-modal';
import { CustomerBookModal } from '@/app/shared/customer-book-modal';
import bookingDemoJson from './available-load-booking-demo.json';
import {
  PUBLIC_SHIPMENT_AVAILABILITY,
  canPublicShipmentBookNow,
  getPublicShipmentAvailability,
  isPublicShipmentBooked,
  isPublicShipmentHeld,
  type PublicShipmentAvailability,
} from './available-load-state';
import './available-loads-table.css';

type DemoBookingLoad = PublicShipment & {
  is_demo: true;
  demo_label: string;
};

// The JSON rows make the unimplemented booking experience reviewable locally.
// They are intentionally absent from production; production renders API data.
const DEMO_BOOKING_LOADS: DemoBookingLoad[] =
  process.env.NODE_ENV === 'development'
    ? (bookingDemoJson.map((load) => ({
        ...(load as PublicShipment),
        is_demo: true as const,
      })) as DemoBookingLoad[])
    : [];

type LoadFilters = {
  search: string;
  pickupCity: string;
  pickupState: string;
  destinationCity: string;
  destinationState: string;
  pickupDateFrom: Date | null;
  pickupDateTo: Date | null;
};

const EMPTY_FILTERS: LoadFilters = {
  search: '',
  pickupCity: '',
  pickupState: '',
  destinationCity: '',
  destinationState: '',
  pickupDateFrom: null,
  pickupDateTo: null,
};

const LOADS_PER_PAGE = 10;

const LOAD_SORT_OPTIONS = [
  { value: 'pickup_date', label: 'Pickup date: soonest' },
  { value: '-pickup_date', label: 'Pickup date: latest' },
];

const EMPTY_PAGINATION: PublicShipmentPagination = {
  total: 0,
  page: 1,
  per_page: LOADS_PER_PAGE,
  page_count: 0,
};

const SKELETON_ROWS = Array.from(
  { length: LOADS_PER_PAGE },
  (_, index) => index
);

const controlClass =
  'h-12 rounded-lg !border border-[#dce4ef] bg-white text-[15px] shadow-none focus:border-[#119df1] focus:ring-[#119df1]/20';

function formatDateParam(date: Date | null) {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatLocation(location: PublicShipment['pickup']) {
  return [location.city, location.state].filter(Boolean).join(', ') || ', ';
}

function parseApiDate(value: string | number | null) {
  if (value === null || value === '') return null;
  const date =
    typeof value === 'number'
      ? new Date(value < 10_000_000_000 ? value * 1000 : value)
      : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatEstimatedTime(value: string | number | null) {
  const date = parseApiDate(value);
  if (!date) return 'Not provided';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function formatNumber(value: number | null, suffix: string) {
  return value === null ? '—' : `${value.toLocaleString('en-US')} ${suffix}`;
}

function formatPrice(value: number | null) {
  return value === null
    ? 'Call for rate'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(value);
}

function equipmentLabel(load: PublicShipment) {
  return (
    load.freight.equipment_type.description ||
    load.freight.equipment_type.code ||
    'Equipment not provided'
  );
}

type AvailableLoadsTableProps = {
  context?: 'landing' | 'dashboard';
  onBidClick?: (load: PublicShipment) => void;
  onBookClick?: (load: PublicShipment) => void;
  onViewClick?: (load: PublicShipment) => void;
};

export function AvailableLoadsTable({
  context = 'landing',
  onBidClick,
  onBookClick,
  onViewClick,
}: AvailableLoadsTableProps) {
  const showView = context === 'dashboard' && Boolean(onViewClick);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loads, setLoads] = useState<PublicShipment[]>([]);
  const [pagination, setPagination] =
    useState<PublicShipmentPagination>(EMPTY_PAGINATION);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('pickup_date');
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBidLoad, setSelectedBidLoad] = useState<PublicShipment | null>(
    null
  );
  const [selectedBookingLoad, setSelectedBookingLoad] =
    useState<DemoBookingLoad | null>(null);
  const [demoLoads, setDemoLoads] =
    useState<DemoBookingLoad[]>(DEMO_BOOKING_LOADS);
  const [selectedCustomerLoad, setSelectedCustomerLoad] =
    useState<PublicShipment | null>(null);
  const [isCustomerBidModalOpen, setIsCustomerBidModalOpen] = useState(false);
  const [isCustomerBookModalOpen, setIsCustomerBookModalOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError('');

    getPublicShipments(
      {
        page,
        perPage: LOADS_PER_PAGE,
        search: filters.search,
        pickupCity: filters.pickupCity,
        pickupState: filters.pickupState,
        destinationCity: filters.destinationCity,
        destinationState: filters.destinationState,
        pickupDateFrom: formatDateParam(filters.pickupDateFrom),
        pickupDateTo: formatDateParam(filters.pickupDateTo),
        sort,
      },
      controller.signal
    )
      .then((response) => {
        setLoads(response.data);
        setPagination(response._meta.pagination);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted) return;
        setLoads([]);
        setPagination(EMPTY_PAGINATION);
        setError(
          requestError instanceof Error
            ? requestError.message
            : 'Available loads could not be loaded.'
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [filters, page, sort]);

  function updateFilter<Key extends keyof LoadFilters>(
    key: Key,
    value: LoadFilters[Key]
  ) {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  }

  function applyFilters(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPage(1);
    setFilters({ ...draftFilters });
  }

  function clearFilters() {
    setDraftFilters(EMPTY_FILTERS);
    setPage(1);
    setFilters(EMPTY_FILTERS);
  }

  async function refreshLoadsAfterBid() {
    try {
      const response = await getPublicShipments({
        page,
        perPage: LOADS_PER_PAGE,
        search: filters.search,
        pickupCity: filters.pickupCity,
        pickupState: filters.pickupState,
        destinationCity: filters.destinationCity,
        destinationState: filters.destinationState,
        pickupDateFrom: formatDateParam(filters.pickupDateFrom),
        pickupDateTo: formatDateParam(filters.pickupDateTo),
        sort,
      });

      setLoads(response.data);
      setPagination(response._meta.pagination);
    } catch {
      // The bid already succeeded. Keep the current list visible if refreshing fails.
    }
  }

  function updateDemoLoadAfterBooking(
    pro: number,
    availability: PublicShipmentAvailability
  ) {
    const nextAvailability =
      availability === PUBLIC_SHIPMENT_AVAILABILITY.AVAILABLE
        ? PUBLIC_SHIPMENT_AVAILABILITY.BOOK_PENDING
        : availability;

    setDemoLoads((current) =>
      current.map((load) =>
        load.pro === pro
          ? {
              ...load,
              availability_status: nextAvailability,
              can_bid: false,
              can_book_now: false,
            }
          : load
      )
    );
  }

  return (
    <section
      className={`available-loads-board ss-section ss-section--soft ${
        context === 'dashboard' ? 'available-loads-board--dashboard' : ''
      }`}
    >
      <div className="container">
        {context !== 'dashboard' && (
          <div
            className={`mb-8 grid grid-cols-1 items-end gap-5 lg:mb-6 lg:grid-cols-[1fr_minmax(280px,0.55fr)] ${'gap-12 xl:pb-12'}`}
          >
            <div>
              <Pill label="Available freight" />
              <h2
                className={`m-0 text-[clamp(32px,9.5vw,38px)] font-semibold leading-none tracking-tight`}
                // style={{
                //   letterSpacing: '-0.055em',
                // }}
              >
                Loads Ready to Move
              </h2>
            </div>
            <p
              className={`m-0 max-w-[520px] text-[16px] leading-relaxed text-neutral-500 lg:text-[17px] ${'lg:leading-[1.65]'}`}
            >
              Search current freight opportunities by route and pickup date.
              Contact dispatch to confirm availability and booking details.
            </p>
          </div>
        )}

        <form
          className="available-loads-board__filters mt-10"
          onSubmit={applyFilters}
        >
          <div className="available-loads-board__filter-bar">
            <Input
              aria-label="Search available loads"
              placeholder="Search by PRO, freight, city..."
              value={draftFilters.search}
              onChange={(event) => updateFilter('search', event.target.value)}
              prefix={
                <PiMagnifyingGlass
                  className="available-loads-board__search-icon"
                  aria-hidden="true"
                />
              }
              inputClassName={controlClass}
            />
            <Select
              aria-label="Sort available loads by pickup date"
              options={LOAD_SORT_OPTIONS}
              value={sort}
              onChange={(value: string) => {
                setPage(1);
                setSort(value);
              }}
              getOptionValue={(option) => option.value}
              displayValue={(value) =>
                LOAD_SORT_OPTIONS.find((option) => option.value === value)
                  ?.label
              }
              suffix={<PiCaretDownBold aria-hidden="true" />}
              className="available-loads-board__sort"
              selectClassName={controlClass}
            />
            <Button
              type="button"
              variant="outline"
              className="available-loads-board__advanced-button"
              aria-expanded={isAdvancedOpen}
              onClick={() => setIsAdvancedOpen((current) => !current)}
            >
              Advanced filters
              <PiCaretDownBold
                className={isAdvancedOpen ? 'rotate-180' : ''}
                aria-hidden="true"
              />
            </Button>
            <div className="available-loads-board__filter-actions">
              <Button
                type="submit"
                className="available-loads-board__search-button"
                disabled={isLoading}
              >
                Search loads
              </Button>
              <Button
                type="button"
                variant="outline"
                className="available-loads-board__clear-button"
                onClick={clearFilters}
              >
                Clear
              </Button>
            </div>
          </div>

          {isAdvancedOpen ? (
            <div className="available-loads-board__advanced-fields">
              <Input
                label="Pickup city"
                placeholder="City"
                value={draftFilters.pickupCity}
                onChange={(event) =>
                  updateFilter('pickupCity', event.target.value)
                }
                inputClassName={controlClass}
              />
              <Input
                label="Pickup state"
                placeholder="State"
                value={draftFilters.pickupState}
                onChange={(event) =>
                  updateFilter('pickupState', event.target.value)
                }
                inputClassName={controlClass}
              />
              <Input
                label="Destination city"
                placeholder="City"
                value={draftFilters.destinationCity}
                onChange={(event) =>
                  updateFilter('destinationCity', event.target.value)
                }
                inputClassName={controlClass}
              />
              <Input
                label="Destination state"
                placeholder="State"
                value={draftFilters.destinationState}
                onChange={(event) =>
                  updateFilter('destinationState', event.target.value)
                }
                inputClassName={controlClass}
              />
              <DatePicker
                selected={draftFilters.pickupDateFrom}
                onChange={(date: Date | null) =>
                  updateFilter('pickupDateFrom', date)
                }
                maxDate={draftFilters.pickupDateTo ?? undefined}
                placeholderText="From date"
                dateFormat="MMM d, yyyy"
                popperClassName="available-loads-board__date-popper"
                inputProps={{
                  label: 'Pickup from',
                  inputClassName: controlClass,
                }}
              />
              <DatePicker
                selected={draftFilters.pickupDateTo}
                onChange={(date: Date | null) =>
                  updateFilter('pickupDateTo', date)
                }
                minDate={draftFilters.pickupDateFrom ?? undefined}
                placeholderText="To date"
                dateFormat="MMM d, yyyy"
                popperClassName="available-loads-board__date-popper"
                inputProps={{
                  label: 'Pickup to',
                  inputClassName: controlClass,
                }}
              />
            </div>
          ) : null}
        </form>

        <div className="available-loads-board__table-toolbar">
          <div className="available-loads-board__summary" aria-live="polite">
            {isLoading ? (
              <span className="sr-only">Loading available loads...</span>
            ) : (
              `${pagination.total.toLocaleString('en-US')} load${
                pagination.total === 1 ? '' : 's'
              } found`
            )}
          </div>
          {context !== 'dashboard' && demoLoads.length ? (
            <span className="available-loads-board__demo-note">
              Demo booking loads are shown above live results.
            </span>
          ) : null}
          <address
            className="available-loads-board__contact"
            aria-label="Dispatch contact information"
          >
            {/* RizzUI has no link primitive; native anchors are required for tel and mailto protocols. */}
            <a href="tel:+13312492786">
              <PiPhoneBold aria-hidden="true" />
              (331)-249-2786
            </a>
            <a href="mailto:operations@ship-solutions.net">
              <PiEnvelopeSimpleBold aria-hidden="true" />
              operations@ship-solutions.net
            </a>
          </address>
        </div>

        <div
          className="available-loads-board__table-wrap"
          aria-busy={isLoading}
        >
          <table
            className={
              showView
                ? 'available-loads-board__table available-loads-board__table--with-view'
                : 'available-loads-board__table'
            }
          >
            <caption className="sr-only">
              Available freight loads with pickup, delivery, mileage, equipment,
              description, weight, and price details
            </caption>
            <thead>
              <tr>
                <th scope="col">Pickup</th>
                <th scope="col">Delivery</th>
                <th scope="col">Miles</th>
                <th scope="col">Freight</th>
                <th scope="col">Description</th>
                <th scope="col">Weight</th>
                <th scope="col">Price</th>
                {showView ? <th scope="col">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {context !== 'dashboard' &&
                demoLoads.map((load) => {
                  const availability = getPublicShipmentAvailability(load);
                  const isBooked = isPublicShipmentBooked(availability);
                  const isHeld = isPublicShipmentHeld(availability);
                  const canBookNow = canPublicShipmentBookNow(load);

                  return (
                    <tr
                      key={`demo-load-${load.pro}`}
                      className={
                        isBooked
                          ? 'available-loads-board__row--demo available-loads-board__row--booked'
                          : isHeld
                            ? 'available-loads-board__row--demo available-loads-board__row--held'
                            : 'available-loads-board__row--demo'
                      }
                    >
                      <td data-label="Pickup">
                        <Badge className="available-loads-board__demo-badge mb-2 rounded-full bg-[#eaf6ff] px-2.5 py-1 text-[11px] font-bold text-[#087ac0]">
                          {load.demo_label}
                        </Badge>
                        <span className="available-loads-board__location">
                          {formatLocation(load.pickup)}
                        </span>
                        <span className="available-loads-board__time">
                          <span>Estimated time</span>
                          {formatEstimatedTime(load.estimated_times.pickup_at)}
                        </span>
                      </td>
                      <td data-label="Delivery">
                        <span className="available-loads-board__location">
                          {formatLocation(load.destination)}
                        </span>
                        <span className="available-loads-board__time">
                          <span>Estimated time</span>
                          {formatEstimatedTime(
                            load.estimated_times.delivery_at
                          )}
                        </span>
                      </td>
                      <td data-label="Miles">
                        {formatNumber(load.miles, 'mi')}
                      </td>
                      <td data-label="Freight">{equipmentLabel(load)}</td>
                      <td data-label="Description">
                        <span className="available-loads-board__description">
                          {load.freight.description?.trim() ||
                            'Description not provided'}
                        </span>
                      </td>
                      <td data-label="Weight">
                        {formatNumber(load.freight.weight, 'lb')}
                      </td>
                      <td data-label="Demo booking">
                        {isBooked ? (
                          <Badge className="available-loads-board__taken-badge rounded-full bg-[#e6edf4] px-3 py-1.5 text-sm font-bold text-[#526174]">
                            Booked
                          </Badge>
                        ) : isHeld ? (
                          <span
                            className="available-loads-board__hold-status"
                            role="status"
                          >
                            Hold — pending approval
                          </span>
                        ) : (
                          <div className="available-loads-board__price-actions">
                            <span className="available-loads-board__price">
                              {formatPrice(load.price)}
                            </span>
                            <Button
                              type="button"
                              size="sm"
                              disabled={!canBookNow}
                              className="available-loads-board__book-now-button"
                              onClick={() => {
                                if (onBookClick) {
                                  onBookClick(load);
                                } else {
                                  setSelectedBookingLoad(load);
                                }
                              }}
                              aria-label={`Try Book Now demo for load ${load.pro} at ${formatPrice(load.price)}`}
                            >
                              Book Now
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              {isLoading
                ? SKELETON_ROWS.map((row) => (
                    <tr
                      key={`load-skeleton-${row}`}
                      className="available-loads-board__skeleton-row"
                      aria-hidden="true"
                    >
                      <td data-label="Pickup">
                        <div className="available-loads-board__skeleton-stack">
                          <Skeleton className="h-5 w-28 rounded-md" />
                          <Skeleton className="h-4 w-36 rounded-md" />
                        </div>
                      </td>
                      <td data-label="Delivery">
                        <div className="available-loads-board__skeleton-stack">
                          <Skeleton className="h-5 w-32 rounded-md" />
                          <Skeleton className="h-4 w-36 rounded-md" />
                        </div>
                      </td>
                      <td data-label="Miles">
                        <Skeleton className="h-5 w-14 rounded-md" />
                      </td>
                      <td data-label="Freight">
                        <Skeleton className="h-5 w-24 rounded-md" />
                      </td>
                      <td data-label="Description">
                        <Skeleton className="h-5 w-40 rounded-md" />
                      </td>
                      <td data-label="Weight">
                        <Skeleton className="h-5 w-24 rounded-md" />
                      </td>
                      <td data-label="Price">
                        <div className="available-loads-board__skeleton-actions">
                          <Skeleton className="h-10 w-[72px] rounded-full" />
                          <Skeleton className="h-10 w-[84px] rounded-full" />
                          <Skeleton className="h-10 w-[96px] rounded-full" />
                        </div>
                      </td>
                      {showView ? (
                        <td data-label="Actions">
                          <Skeleton className="h-8 w-16 rounded-full" />
                        </td>
                      ) : null}
                    </tr>
                  ))
                : null}
              {!isLoading && !error && loads.length === 0 ? (
                <tr>
                  <td
                    className="available-loads-board__empty"
                    colSpan={showView ? 8 : 7}
                  >
                    <Empty
                      image={
                        <span className="available-loads-board__empty-icon">
                          <PiMagnifyingGlassMinus aria-hidden="true" />
                        </span>
                      }
                      text="No live loads found"
                      className="available-loads-board__empty-state"
                      textClassName="available-loads-board__empty-title"
                    >
                      <p className="available-loads-board__empty-copy">
                        Try changing or clearing your search filters.
                      </p>
                    </Empty>
                  </td>
                </tr>
              ) : null}
              {!isLoading && error ? (
                <tr>
                  <td
                    className="available-loads-board__empty"
                    colSpan={showView ? 8 : 7}
                  >
                    {error}
                  </td>
                </tr>
              ) : null}
              {!isLoading &&
                loads.map((load) => {
                  const availability = getPublicShipmentAvailability(load);
                  const isBooked = isPublicShipmentBooked(availability);
                  const isHeld = isPublicShipmentHeld(availability);
                  const canBookNow = canPublicShipmentBookNow(load);

                  return (
                    <tr
                      key={load.pro}
                      className={
                        isBooked
                          ? 'available-loads-board__row--booked'
                          : isHeld
                            ? 'available-loads-board__row--held'
                            : ''
                      }
                    >
                      <td data-label="Pickup">
                        <span className="available-loads-board__location">
                          {formatLocation(load.pickup)}
                        </span>
                        <span className="available-loads-board__time">
                          <span>Estimated time</span>
                          {formatEstimatedTime(load.estimated_times.pickup_at)}
                        </span>
                      </td>
                      <td data-label="Delivery">
                        <span className="available-loads-board__location">
                          {formatLocation(load.destination)}
                        </span>
                        <span className="available-loads-board__time">
                          <span>Estimated time</span>
                          {formatEstimatedTime(
                            load.estimated_times.delivery_at
                          )}
                        </span>
                      </td>
                      <td data-label="Miles">
                        {formatNumber(load.miles, 'mi')}
                      </td>
                      <td data-label="Freight">{equipmentLabel(load)}</td>
                      <td data-label="Description">
                        <span className="available-loads-board__description">
                          {load.freight.description?.trim() ||
                            'Description not provided'}
                        </span>
                      </td>
                      <td data-label="Weight">
                        {formatNumber(load.freight.weight, 'lb')}
                      </td>
                      <td data-label="Price">
                        {isBooked ? (
                          <Badge className="available-loads-board__taken-badge rounded-full bg-[#e6edf4] px-3 py-1.5 text-sm font-bold text-[#526174]">
                            Booked
                          </Badge>
                        ) : isHeld ? (
                          <span
                            className="available-loads-board__hold-status"
                            role="status"
                          >
                            Hold — pending approval
                          </span>
                        ) : (
                          <div className="available-loads-board__price-actions">
                            <Button
                              type="button"
                              size="sm"
                              className="available-loads-board__bid-button"
                              onClick={() => {
                                if (onBidClick) {
                                  onBidClick(load);
                                } else {
                                  setSelectedBidLoad(load);
                                }
                              }}
                              aria-label={`Bid on load ${load.pro} from ${formatLocation(load.pickup)} to ${formatLocation(load.destination)}`}
                            >
                              <PiGavelBold aria-hidden="true" />
                              Bid
                            </Button>
                            {canBookNow && (
                              <Button
                                type="button"
                                size="sm"
                                className="available-loads-board__book-now-button"
                                onClick={() => {
                                  if (onBookClick) {
                                    onBookClick(load);
                                  } else {
                                    setSelectedBookingLoad(
                                      load as DemoBookingLoad
                                    );
                                  }
                                }}
                                aria-label={`Book load ${load.pro} at ${formatPrice(load.price)}`}
                              >
                                Book Now
                              </Button>
                            )}
                            <span className="available-loads-board__price">
                              {formatPrice(load.price)}
                            </span>
                          </div>
                        )}
                      </td>
                      {showView ? (
                        <td data-label="Actions">
                          <Button
                            type="button"
                            size="sm"
                            aria-label={`View load ${load.pro}`}
                            onClick={() => onViewClick?.(load)}
                            className="available-loads-board__view-button rounded-full px-4 py-1.5 text-xs font-bold shadow-none"
                          >
                            View
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {pagination.total > 0 ? (
          <div className="available-loads-board__pagination">
            <Button
              type="button"
              variant="outline"
              disabled={page <= 1 || isLoading}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              <PiArrowLeft aria-hidden="true" /> Previous
            </Button>
            <span>
              Page {pagination.page} of {Math.max(1, pagination.page_count)}
            </span>
            <Button
              type="button"
              variant="outline"
              disabled={page >= pagination.page_count || isLoading}
              onClick={() => setPage((current) => current + 1)}
            >
              Next <PiArrowRight aria-hidden="true" />
            </Button>
          </div>
        ) : null}

        <p className="available-loads-board__note">
          Load availability and rates are subject to carrier qualification and
          dispatch confirmation.
        </p>
      </div>
      <AvailableLoadBidModal
        load={selectedBidLoad}
        onClose={() => setSelectedBidLoad(null)}
        onBidSubmitted={refreshLoadsAfterBid}
      />
      <AvailableLoadBookingModal
        load={selectedBookingLoad}
        onClose={() => setSelectedBookingLoad(null)}
        onBookingSubmitted={({ pro, availability }) => {
          updateDemoLoadAfterBooking(pro, availability);
        }}
      />
    </section>
  );
}
